import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useNotification } from '../../contexts/NotificationContext';
import ModernEmailBuilder from '../emailBuilder/ModernEmailBuilder';
import { EmailTemplate as BuilderEmailTemplate, EmailTemplateStyles } from '../../types/emailBuilder';
import { getEmailTemplate, updateEmailTemplate } from '../../services/emailTemplates';
import { generateEmailHTML } from '../emailBuilder/utils/emailHTMLGenerator';
import { Timestamp } from 'firebase/firestore';

const DEFAULT_STYLES: EmailTemplateStyles = {
  containerWidth: '600px',
  backgroundColor: '#ffffff',
  fontFamily: 'Arial, sans-serif',
  contentBackgroundColor: '#ffffff',
  padding: '0px',
  textColor: '#111827',
};

const toDate = (value: any): Date => {
  if (!value) return new Date();
  if (value instanceof Date) return value;
  if (value?.toDate) {
    try {
      return value.toDate();
    } catch {
      return new Date();
    }
  }
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? new Date() : parsed;
};

const normalizeDesignPayload = (design: any): any => {
  if (!design) return null;
  if (typeof design === 'string') {
    try {
      return normalizeDesignPayload(JSON.parse(design));
    } catch {
      return null;
    }
  }
  if (design?.designJson) {
    return normalizeDesignPayload(design.designJson);
  }
  if (design?.state) {
    return normalizeDesignPayload(design.state);
  }
  return design;
};

const AdminTemplateBuilder: React.FC = () => {
  const { templateId } = useParams<{ templateId?: string }>();
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const { showSuccess, showError } = useNotification();

  const [template, setTemplate] = useState<BuilderEmailTemplate | null>(null);
  const [loading, setLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  
  // Template metadata
  const [templateName, setTemplateName] = useState('Untitled Template');
  const [templateDescription, setTemplateDescription] = useState('');
  const [templateSubject, setTemplateSubject] = useState('');
  const [templateCategory, setTemplateCategory] = useState<'newsletter' | 'promotional' | 'transactional' | 'welcome' | 'announcement' | 'other'>('newsletter');
  const [templateTags, setTemplateTags] = useState<string[]>([]);
  const [isActive, setIsActive] = useState(true);

  useEffect(() => {
    if (templateId) {
      loadTemplate();
    } else {
      setLoading(false);
    }
  }, [templateId]);

  const loadTemplate = async () => {
    if (!templateId) return;
    
    try {
      setLoading(true);
      const existingTemplate = templateId ? await getEmailTemplate(templateId) : null;
      if (!existingTemplate) {
        showError('Template not found');
        navigate('/admin/control-panel-7x9k2m/email-templates');
        return;
      }

      const normalizedDesign = normalizeDesignPayload(existingTemplate.designJson) || {
        blocks: [],
        styles: DEFAULT_STYLES,
      };

      const builderTemplate: BuilderEmailTemplate = {
        id: existingTemplate.id || '',
        name: existingTemplate.name || 'Untitled Template',
        subject: existingTemplate.subject || '',
        styles: normalizedDesign.styles || DEFAULT_STYLES,
        htmlContent: existingTemplate.htmlContent,
        designJson: normalizedDesign,
        createdAt: toDate(existingTemplate.createdAt),
        updatedAt: toDate(existingTemplate.updatedAt),
      };

      setTemplate(builderTemplate);
      setTemplateName(existingTemplate.name || 'Untitled Template');
      setTemplateDescription(existingTemplate.description || '');
      setTemplateSubject(existingTemplate.subject || '');
      setTemplateCategory((existingTemplate.category as any) || 'newsletter');
      setTemplateTags(existingTemplate.tags || []);
      setIsActive(existingTemplate.isActive !== false);
    } catch (error) {
      console.error('Error loading template:', error);
      showError('Failed to load template');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async (templateData: BuilderEmailTemplate) => {
    if (!currentUser) {
      showError('You must be logged in to save templates');
      return;
    }

    try {
      setIsSaving(true);

      // Generate HTML content
      const htmlContent = generateEmailHTML(
        (templateData.designJson as any)?.blocks || [],
        templateData.styles || DEFAULT_STYLES
      );

      if (!templateId) {
        showError('Template ID missing. Use the create flow to add new templates.');
        return;
      }

      await updateEmailTemplate(templateId, {
        name: templateName,
        description: templateDescription,
        subject: templateSubject,
        htmlContent,
        textContent: templateSubject,
        category: templateCategory as any,
        tags: templateTags,
        isActive,
        designJson: {
          blocks: (templateData.designJson as any)?.blocks || [],
          styles: templateData.styles || DEFAULT_STYLES,
        },
        updatedAt: Timestamp.now(),
      });

      showSuccess('Template updated successfully');
    } catch (error) {
      console.error('Error saving template:', error);
      showError('Failed to save template');
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancel = () => {
    if (window.confirm('Are you sure you want to leave? Unsaved changes will be lost.')) {
      navigate('/templates');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-ocean-500 mx-auto"></div>
          <p className="mt-4 text-slate-600">Loading template...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 flex flex-col bg-slate-50">
      {/* Header with Metadata */}
      {/* Email Builder */}
      <div className="flex-1 overflow-hidden">
        <ModernEmailBuilder
          template={template || undefined}
          campaignName={templateName}
          onSave={handleSave}
          onManualSaveComplete={() => navigate('/admin/control-panel-7x9k2m/email-templates')}
          onCancel={handleCancel}
        />
      </div>

    </div>
  );
};

export default AdminTemplateBuilder;

